import React, { createContext, useContext, useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, obj = signed in
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        try {
            const { data } = await http.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    // Listen for the global "auth:expired" event fired by the axios interceptor
    // when any authenticated request returns 401. Clears local state so the
    // login gate reappears — no manual F5 refresh needed.
    useEffect(() => {
        const onExpired = () => setUser(false);
        window.addEventListener("auth:expired", onExpired);
        return () => window.removeEventListener("auth:expired", onExpired);
    }, []);

    const login = async (email, password) => {
        try {
            const { data } = await http.post("/auth/login", { email, password });
            setUser(data);
            return { ok: true };
        } catch (e) {
            const detail = e?.response?.data?.detail;
            return { ok: false, error: typeof detail === "string" ? detail : formatApiError(e) };
        }
    };

    const register = async (email, password, firstName, lastName, newsletter = false) => {
        try {
            const { data } = await http.post("/auth/register", {
                email,
                password,
                first_name: firstName,
                last_name: lastName,
                newsletter,
            });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const logout = async () => {
        await http.post("/auth/logout");
        setUser(false);
    };

    return (
        <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
